import React from "react";
import { clsx } from "clsx";
import { UnitItemContext } from "@/context/UnitItemContext";
import { UnitPromotions } from "@/components/units/unit/info/UnitPromotions";
import { UnitFeatures } from "@/components/units/unit/info/UnitFeatures";
import { UnitUrgency } from "@/components/units/unit/info/UnitUrgency";
import { UnitPrice } from "@/components/units/unit/info/UnitPrice";
import { UnitImage } from "@/components/units/unit/info/UnitImage";
import { NearbyCollapse } from "@/components/units/unit/info/NearbyCollapse";
import type { UnitGroup } from "@/types";
import { Button } from "@/components/ui/button";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { UnitAction } from "./info/UnitAction";
import { useUnitDataAttributes } from "@/hooks/useUnitDataAttributes";

interface UnitItemProps {
    unitGroup: UnitGroup;
    layout: "grid" | "row";
}

export const UnavailableUnitItem = ({
    unitGroup,
    layout = "row",
}: UnitItemProps) => {
    const { representative_unit, rates, available, show_nearby_units } =
        unitGroup;
    const [selectedPromo, setSelectedPromo] = React.useState<number>(() => {
        if (rates.promo_rates.length === 1 || rates.promo_rates.length > 1) {
            return rates.promo_rates[0].id;
        } else {
            return 0;
        }
    });
    const dataAttributes = useUnitDataAttributes(unitGroup);

    const unitItemInfo = {
        unitGroup,
        selectedPromo,
        setSelectedPromo,
    };

    const [isOpen, setIsOpen] = React.useState(false);
    const toggleNearby = () => setIsOpen((prev) => !prev);

    return (
        <UnitItemContext.Provider value={unitItemInfo}>
            <NearbyCollapse
                isOpen={isOpen}
                onToggle={toggleNearby}
                className={clsx("unit-item", "unit-item--" + layout)}
                dataAttributes={dataAttributes}
            >
                <div className="unit-item__image">
                    <UnitImage
                        length={representative_unit.length}
                        width={representative_unit.width}
                    />
                </div>
                <div className="unit-item__dimension">
                    {`${representative_unit.width}' x ${representative_unit.length}'`}
                </div>
                <div className="unit-item__category">
                    {representative_unit.category?.name ?? ""}
                </div>
                <div className="unit-item__features">
                    <UnitFeatures features={representative_unit.features} />
                </div>
                <div className="unit-item__promotions">
                    <UnitPromotions promotions={rates.promo_rates} />
                </div>
                <div className="unit-item__urgency">
                    <UnitUrgency available={available} />
                </div>
                <div className="unit-item__price">
                    <UnitPrice rates={rates} />
                </div>
                <div className="unit-item__action">
                    <UnitAction />
                    {show_nearby_units && (
                        <CollapsibleTrigger asChild>
                            <Button
                                variant={"default"}
                                className="find-nearby-btn"
                            >
                                Find Nearby
                            </Button>
                        </CollapsibleTrigger>
                    )}
                </div>
            </NearbyCollapse>
        </UnitItemContext.Provider>
    );
};
